import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import { contentGalleryRepository } from '../repositories/contentGallery.repository';
import { contentKeys } from '../services/content.keys';
import type { ContentImage } from '../types/content.types';

export const useContentImages = (campaignId: string) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  return useQuery({
    queryKey: contentKeys.images(orgId, campaignId),
    queryFn: async (): Promise<ContentImage[]> => {
      if (!campaignId || !orgId) return [];
      return contentGalleryRepository.getImages(orgId, campaignId);
    },
    enabled: !!campaignId && !!orgId,
  });
};

export const useUploadContentImage = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      file,
      campaignId,
      organizationId,
      folderId,
    }: {
      file: File;
      campaignId: string;
      organizationId: string;
      folderId?: string;
    }) => {
      const orgId = organizationId || currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');

      return contentGalleryRepository.uploadImage({
        file,
        organizationId: orgId,
        campaignId,
        folderId,
      });
    },
    onSuccess: (_, variables) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: contentKeys.images(orgId, variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyImages(variables.campaignId) });
      toast({
        title: 'Success',
        description: 'Image uploaded successfully',
      });
    },
    onError: (error) => {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload image',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteContentImage = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      imageId,
      filePath,
      campaignId,
    }: {
      imageId: string;
      filePath: string;
      campaignId: string;
    }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await contentGalleryRepository.deleteImage(orgId, imageId, filePath);
      return { imageId, campaignId };
    },
    onSuccess: (_, variables) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: contentKeys.images(orgId, variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyImages(variables.campaignId) });
      toast({
        title: 'Success',
        description: 'Image deleted successfully',
      });
    },
    onError: (error) => {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete image',
        variant: 'destructive',
      });
    },
  });
};

export const useContentImageUrl = (filePath: string) => {
  return useQuery({
    queryKey: ['content-image-signed-url', filePath],
    queryFn: async (): Promise<string> => {
      if (!filePath) return '';
      return contentGalleryRepository.getImageSignedUrl(filePath, 3600);
    },
    enabled: !!filePath,
    staleTime: 1000 * 60 * 50, // 50 minutes fresh
    gcTime: 1000 * 60 * 60, // 1 hour in cache
  });
};

export const useGetImageUrl = () => {
  return {
    getSignedImageUrl: async (filePath: string) => {
      return contentGalleryRepository.getImageSignedUrl(filePath, 3600);
    },
  };
};
